import { useState, useCallback, useRef, useEffect } from "react";
import useCyclePurchaseEffectApi from "@apis/useCyclePurchaseEffectApi";
import { parseDate } from "@util/moment";

/**
 * 循環訂單變更單生效日期與下次出貨日期計算 Hook
 * 負責處理變更單的 effectDate 和 nextShippingDate 計算邏輯
 *
 * @param {Object} options - 設定選項
 * @param {number} [options.debounceMs=300] - 防抖延遲時間（毫秒）
 * @returns {Object} 計算結果和取得函數
 */
const useCyclePurchaseEffectRange = ({ debounceMs = 300 } = {}) => {
	const { postCyclePurchaseEffectRangeApi } = useCyclePurchaseEffectApi();

	// 生效日期和下次出貨日期
	const [effectRange, setEffectRange] = useState({
		effectDate: parseDate(), // 生效日，預設今天
		nextShippingDate: "", // 預計出貨日
		lastShippingDate: "", // 最後出貨日
	});

	const debounceTimerRef = useRef(null);

	/**
	 * 取得變更單的生效日期和下次出貨日期
	 * @param {Object} params - 變更單參數（包含 effectType、effectDate、cycleOrderID 等）
	 * @returns {Promise<Object|null>} 計算結果 { effectDate, nextShippingDate } 或 null
	 */
	const getEffectRange = useCallback(
		async params => {
			if (!params || !params.effectType || params.effectType.length === 0) {
				// 如果沒有 effectType，不調用 API
				return null;
			}

			try {
				const resp = await postCyclePurchaseEffectRangeApi(params);
				if (resp) {
					setEffectRange(resp);
					return resp;
				}
				return null;
			} catch (error) {
				console.error("取得變更單生效日期失敗:", error);
				return null;
			}
		},
		// eslint-disable-next-line
		[postCyclePurchaseEffectRangeApi],
	);

	/**
	 * 防抖版本的取得函數（適用於表單輸入時頻繁調用）
	 * @param {Object} params - 變更單參數
	 */
	const getEffectRangeDebounced = useCallback(
		params => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			debounceTimerRef.current = setTimeout(() => {
				getEffectRange(params);
			}, debounceMs);
		},
		// eslint-disable-next-line
		[getEffectRange, debounceMs],
	);

	// 清理定時器
	useEffect(
		() => {
			return () => {
				if (debounceTimerRef.current) {
					clearTimeout(debounceTimerRef.current);
				}
			};
		},
		// eslint-disable-next-line
		[],
	);

	return {
		// 資料
		effectDate: effectRange.effectDate,
		nextShippingDate: effectRange.nextShippingDate,
		lastShippingDate: effectRange.lastShippingDate,
		// 方法
		getEffectRange,
		getEffectRangeDebounced,
	};
};

export default useCyclePurchaseEffectRange;
